import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SaveTenantBusinessProfileRequest, TenantBusinessProfileView } from '@optiflow/types';
import { api } from '@/services/apiClient';

const KEY = ['tenant', 'business-profile'] as const;

/** Declares multipart explicitly: with the client's JSON default, axios would turn FormData into JSON and drop the file; the browser then writes the boundary. */
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export function useBusinessProfileQuery() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<TenantBusinessProfileView>('/tenant/business-profile'),
  });
}

/** Every mutation answers with the whole profile, which replaces the cached one. */
function useProfileMutation<TInput>(send: (input: TInput) => Promise<TenantBusinessProfileView>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: (profile) => queryClient.setQueryData(KEY, profile),
  });
}

export function useSaveBusinessProfileMutation() {
  return useProfileMutation((body: SaveTenantBusinessProfileRequest) =>
    api.put<TenantBusinessProfileView>('/tenant/business-profile', body),
  );
}

export function useUploadLogoMutation() {
  return useProfileMutation((file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<TenantBusinessProfileView>('/tenant/business-profile/logo', form, MULTIPART);
  });
}

export function useRemoveLogoMutation() {
  return useProfileMutation(() =>
    api.delete<TenantBusinessProfileView>('/tenant/business-profile/logo'),
  );
}
